import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Activity, Users, Building2, Clock, Search, MessageCircle, RefreshCw, Wifi, WifiOff, FileDown, FileText } from "lucide-react";
import { labelForPath, formatDuration, timeAgo, isOnline } from "@/lib/activity-utils";
import QuickContactDialog from "@/components/admin/activity/QuickContactDialog";
import { exportToCSV } from "@/lib/export-utils";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useRef } from "react";
import { toast } from "@/hooks/use-toast";

interface SessionRow {
  id: string;
  user_id: string;
  nursery_id: string | null;
  nursery_name: string | null;
  user_name: string | null;
  user_role: string | null;
  current_path: string | null;
  session_start: string;
  last_seen_at: string;
}

interface NurseryAggRow {
  nursery_id: string;
  nursery_name: string;
  owner_id: string | null;
  owner_phone: string | null;
  last_seen_at: string | null;
  current_path: string | null;
  user_count_online: number;
  total_today_seconds: number;
}

const AdminActivity = () => {
  const [search, setSearch] = useState("");
  const [tick, setTick] = useState(0); // forces re-render every 30s for "since" labels
  const [contactTarget, setContactTarget] = useState<NurseryAggRow | null>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  // Live sessions
  const { data: sessions = [], refetch: refetchSessions } = useQuery({
    queryKey: ["admin-activity-sessions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_activity_sessions")
        .select("*")
        .order("last_seen_at", { ascending: false });
      if (error) throw error;
      return (data || []) as SessionRow[];
    },
    refetchInterval: 30_000,
  });

  // All nurseries with owner contact
  const { data: nurseries = [] } = useQuery({
    queryKey: ["admin-activity-nurseries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nurseries")
        .select("id, name, owner_id");
      if (error) throw error;
      const ownerIds = (data || []).map((n: any) => n.owner_id).filter(Boolean);
      let phones: Record<string, string | null> = {};
      if (ownerIds.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("user_id, phone")
          .in("user_id", ownerIds);
        (profs || []).forEach((p: any) => (phones[p.user_id] = p.phone));
      }
      return (data || []).map((n: any) => ({
        id: n.id as string,
        name: n.name as string,
        owner_id: n.owner_id as string | null,
        owner_phone: n.owner_id ? phones[n.owner_id] : null,
      }));
    },
  });

  // Today aggregate from log
  const { data: todayLog = [] } = useQuery({
    queryKey: ["admin-activity-today-log"],
    queryFn: async () => {
      const since = new Date();
      since.setHours(0, 0, 0, 0);
      const { data, error } = await supabase
        .from("user_activity_log")
        .select("nursery_id, duration_seconds")
        .gte("visited_at", since.toISOString());
      if (error) throw error;
      return (data || []) as Array<{ nursery_id: string | null; duration_seconds: number }>;
    },
    refetchInterval: 60_000,
  });

  // Realtime subscription
  useEffect(() => {
    const ch = supabase
      .channel("admin-activity")
      .on("postgres_changes", { event: "*", schema: "public", table: "user_activity_sessions" }, () => {
        refetchSessions();
      })
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [refetchSessions]);

  // Aggregate nurseries
  const nurseryRows: NurseryAggRow[] = useMemo(() => {
    const todaySecondsByNursery = new Map<string, number>();
    todayLog.forEach((row) => {
      if (!row.nursery_id) return;
      todaySecondsByNursery.set(
        row.nursery_id,
        (todaySecondsByNursery.get(row.nursery_id) || 0) + (row.duration_seconds || 0)
      );
    });

    return nurseries
      .map((n) => {
        const nurserySessions = sessions.filter((s) => s.nursery_id === n.id);
        const online = nurserySessions.filter((s) => isOnline(s.last_seen_at));
        const latest = nurserySessions.sort(
          (a, b) => new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime()
        )[0];
        return {
          nursery_id: n.id,
          nursery_name: n.name,
          owner_id: n.owner_id,
          owner_phone: n.owner_phone,
          last_seen_at: latest?.last_seen_at || null,
          current_path: latest?.current_path || null,
          user_count_online: online.length,
          total_today_seconds: todaySecondsByNursery.get(n.id) || 0,
        };
      })
      .filter((n) =>
        search.trim() ? n.nursery_name.toLowerCase().includes(search.trim().toLowerCase()) : true
      )
      .sort((a, b) => {
        // online first, then by last_seen
        if (a.user_count_online !== b.user_count_online) return b.user_count_online - a.user_count_online;
        const ta = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0;
        const tb = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0;
        return tb - ta;
      });
  }, [nurseries, sessions, todayLog, search, tick]);

  const onlineSessions = sessions.filter((s) => isOnline(s.last_seen_at));
  const activeNurseriesToday = nurseryRows.filter((n) => n.total_today_seconds > 0).length;
  const totalTodaySeconds = todayLog.reduce((acc, r) => acc + (r.duration_seconds || 0), 0);

  const handleExportCSV = () => {
    const today = new Date().toISOString().slice(0, 10);
    const summary = [
      { البند: "متصلون الآن", القيمة: String(onlineSessions.length) },
      { البند: "حضانات نشطة اليوم", القيمة: String(activeNurseriesToday) },
      { البند: "إجمالي الجلسات", القيمة: String(sessions.length) },
      { البند: "إجمالي وقت اليوم", القيمة: formatDuration(totalTodaySeconds) },
    ];
    exportToCSV(summary as any, `ملخص-نشاط-الحضانات-${today}.csv`);
  };

  const handleExportPDF = async () => {
    if (!summaryRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(summaryRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      pdf.save(`ملخص-نشاط-الحضانات-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch {
      toast({ title: "تعذر إنشاء ملف PDF", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div dir="rtl" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="w-6 h-6 text-primary" />
            نشاط الحضانات الحي
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            تابع المستخدمين المتصلين الآن، آخر نشاط، ومكانهم في بناء الحضانة — مع إمكانية إرسال رسالة دعم فوراً.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-2">
            <FileDown className="w-4 h-4" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={exporting} className="gap-2">
            <FileText className="w-4 h-4" /> {exporting ? "جاري..." : "PDF"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => refetchSessions()} className="gap-2">
            <RefreshCw className="w-4 h-4" /> تحديث
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div ref={summaryRef} dir="rtl" className="bg-background p-3 rounded-lg">
        <h2 className="text-lg font-bold mb-3">ملخص النشاط — {new Date().toLocaleDateString("ar-EG")}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={<Wifi className="w-5 h-5 text-green-600" />} label="متصلون الآن" value={onlineSessions.length} />
          <StatCard icon={<Building2 className="w-5 h-5 text-primary" />} label="حضانات نشطة اليوم" value={activeNurseriesToday} />
          <StatCard icon={<Users className="w-5 h-5 text-amber-600" />} label="إجمالي الجلسات" value={sessions.length} />
          <StatCard
            icon={<Clock className="w-5 h-5 text-blue-600" />}
            label="إجمالي وقت اليوم"
            value={formatDuration(totalTodaySeconds)}
          />
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="ابحث باسم الحضانة..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pr-9"
        />
      </div>

      <Tabs defaultValue="online" className="space-y-4">
        <TabsList>
          <TabsTrigger value="online" className="gap-1.5">
            <Wifi className="w-4 h-4" /> المتصلون الآن ({onlineSessions.length})
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-1.5">
            <Building2 className="w-4 h-4" /> كل الحضانات ({nurseryRows.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="online">
          <Card>
            <CardHeader><CardTitle className="text-base">جلسات نشطة الآن</CardTitle></CardHeader>
            <CardContent className="p-0">
              {onlineSessions.length === 0 ? (
                <p className="text-center py-12 text-muted-foreground text-sm">لا يوجد متصلون الآن</p>
              ) : (
                <div className="divide-y">
                  {onlineSessions.map((s) => {
                    const { label, step } = labelForPath(s.current_path);
                    const sessionMin = Math.floor(
                      (Date.now() - new Date(s.session_start).getTime()) / 1000
                    );
                    const owner = nurseries.find((n) => n.id === s.nursery_id);
                    return (
                      <div key={s.id} className="p-3 flex items-center gap-3 flex-wrap">
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">
                            {s.user_name || "—"}
                            {s.nursery_name && <span className="text-muted-foreground font-normal"> · {s.nursery_name}</span>}
                          </div>
                          <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-1.5 mt-0.5">
                            <Badge variant="outline" className="text-[10px] py-0 h-4">{s.user_role || "—"}</Badge>
                            <span>📍 {label}</span>
                            {step && <Badge variant="secondary" className="text-[10px] py-0 h-4">{step}</Badge>}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground text-left">
                          <div>الجلسة: {formatDuration(sessionMin)}</div>
                          <div>{timeAgo(s.last_seen_at)}</div>
                        </div>
                        {s.nursery_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1.5"
                            onClick={() =>
                              setContactTarget({
                                nursery_id: s.nursery_id!,
                                nursery_name: s.nursery_name || owner?.name || "",
                                owner_id: owner?.owner_id || null,
                                owner_phone: owner?.owner_phone || null,
                                last_seen_at: s.last_seen_at,
                                current_path: s.current_path,
                                user_count_online: 1,
                                total_today_seconds: 0,
                              })
                            }
                          >
                            <MessageCircle className="w-3.5 h-3.5" /> تواصل
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="all">
          <Card>
            <CardHeader><CardTitle className="text-base">سجل كل الحضانات</CardTitle></CardHeader>
            <CardContent className="p-0">
              {nurseryRows.length === 0 ? (
                <p className="text-center py-12 text-muted-foreground text-sm">لا توجد نتائج</p>
              ) : (
                <div className="divide-y">
                  {nurseryRows.map((n) => {
                    const { label, step } = labelForPath(n.current_path);
                    const online = n.user_count_online > 0;
                    return (
                      <div key={n.nursery_id} className="p-3 flex items-center gap-3 flex-wrap">
                        {online ? (
                          <Wifi className="w-4 h-4 text-green-600" />
                        ) : (
                          <WifiOff className="w-4 h-4 text-muted-foreground" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{n.nursery_name}</div>
                          <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                            <span>📍 {label}</span>
                            {step && <span className="text-primary">· {step}</span>}
                            <span>· آخر دخول: {timeAgo(n.last_seen_at)}</span>
                            <span>· وقت اليوم: {formatDuration(n.total_today_seconds)}</span>
                            {online && (
                              <Badge className="bg-green-600 hover:bg-green-600 text-white text-[10px] h-4 py-0">
                                {n.user_count_online} متصل
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5"
                          onClick={() => setContactTarget(n)}
                        >
                          <MessageCircle className="w-3.5 h-3.5" /> رسالة
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <QuickContactDialog
        open={!!contactTarget}
        onOpenChange={(v) => !v && setContactTarget(null)}
        nurseryId={contactTarget?.nursery_id || null}
        nurseryName={contactTarget?.nursery_name || null}
        ownerUserId={contactTarget?.owner_id || null}
        ownerPhone={contactTarget?.owner_phone || null}
        currentStep={contactTarget ? labelForPath(contactTarget.current_path).step : undefined}
      />
    </div>
  );
};

const StatCard = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: number | string }) => (
  <Card>
    <CardContent className="p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-xl font-bold">{value}</div>
      </div>
    </CardContent>
  </Card>
);

export default AdminActivity;
